import { reactive, computed, watchEffect } from 'vue'
import { createInjectionState } from '@vueuse/shared'
import { usePreferredDark } from '@vueuse/core'

type ThemeMode = 'dark' | 'light'

type ThemeConfig = {
  mode: ThemeMode
}

/**
 * Tema según preferencia del sistema (prefers-color-scheme).
 * Aplica clases `light` / `dark` en `<html>` para Tailwind y PrimeVue.
 * No persiste elección manual: siempre sigue al SO/navegador.
 */
const [useProvideTheme, injectModule] = createInjectionState(() => {
  const prefersDark = usePreferredDark()

  const themeConfig = reactive<ThemeConfig>({
    mode: 'light',
  })

  const applyToDocument = (mode: ThemeMode) => {
    if (typeof document === 'undefined') return
    const html = document.documentElement
    html.classList.remove('light', 'dark')
    html.classList.add(mode)
  }

  watchEffect(() => {
    const mode: ThemeMode = prefersDark.value ? 'dark' : 'light'
    themeConfig.mode = mode
    applyToDocument(mode)
  })

  const isDarkTheme = computed(() => themeConfig.mode === 'dark')
  const isLightTheme = computed(() => themeConfig.mode === 'light')

  return {
    themeConfig,
    isDarkTheme,
    isLightTheme,
  }
})

export { useProvideTheme }

export const useTheme = () => {
  const module = injectModule()

  if (module == null)
    throw new Error(
      'Please call `useProvideTheme` on the appropriate parent component',
    )

  return module
}
